import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnTransform } from '@/types/query';
import { getQueryWorker } from '@/engine/worker-client';

type MapTransform = Extract<ColumnTransform, { kind: 'map' }>;

type Props = {
  datasetId: string;
  column: string;
  value?: MapTransform;
  onChange: (next: MapTransform | null) => void;
};

const DISTINCT_LIMIT = 100_000;

/**
 * Bulk-select editor: tick distinct values on the left, assign them a
 * target label on the right. One target label can absorb many sources —
 * the underlying `mapping[src] = label` storage supports that natively.
 */
export function MapEditor({
  datasetId,
  column,
  value,
  onChange,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [mapping, setMapping] = useState<Record<string, string>>(
    () => ({ ...(value?.mapping ?? {}) }),
  );
  // Fallback / otherLabel UI was removed in Phase 10 step 6. The fields stay
  // in the persisted shape so older dashboards round-trip unchanged, but the
  // editor no longer exposes a way to pick "Diğer" or rename the fallback —
  // users said the section was clutter. New groupings default to `as_is`.
  const fallback: 'as_is' | 'other' = value?.fallback ?? 'as_is';
  const otherLabel = value?.otherLabel ?? '';

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [newLabel, setNewLabel] = useState('');
  const [search, setSearch] = useState('');

  const [distinct, setDistinct] = useState<string[]>([]);
  const [distinctLoading, setDistinctLoading] = useState(false);

  // Re-seed when switching column (props.value changes with column).
  useEffect(() => {
    setMapping({ ...(value?.mapping ?? {}) });
    setSelected(new Set());
    setNewLabel('');
    setSearch('');
  }, [column, value]);

  useEffect(() => {
    let cancelled = false;
    setDistinctLoading(true);
    getQueryWorker()
      .distinctValues({ source: datasetId, column, limit: DISTINCT_LIMIT })
      .then((res) => {
        if (cancelled) return;
        setDistinct(res.values.map((v) => v.value));
      })
      .catch(() => {
        if (cancelled) return;
        setDistinct([]);
      })
      .finally(() => {
        if (!cancelled) setDistinctLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [datasetId, column]);

  function emit(
    nextMapping: Record<string, string>,
    nextFallback: 'as_is' | 'other',
    nextOther: string,
  ): void {
    const isEmpty = Object.keys(nextMapping).length === 0;
    if (isEmpty && nextFallback === 'as_is') {
      onChange(null);
      return;
    }
    const next: MapTransform = {
      kind: 'map',
      column,
      mapping: nextMapping,
      fallback: nextFallback,
      ...(nextFallback === 'other' && nextOther.trim()
        ? { otherLabel: nextOther.trim() }
        : {}),
    };
    onChange(next);
  }

  function toggleSelected(v: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  }

  function selectAllVisible(values: string[]): void {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const v of values) next.add(v);
      return next;
    });
  }

  function clearSelection(): void {
    setSelected(new Set());
  }

  function groupSelected(): void {
    const label = newLabel.trim();
    if (!label || selected.size === 0) return;
    const next = { ...mapping };
    for (const src of selected) {
      next[src] = label;
    }
    setMapping(next);
    setSelected(new Set());
    setNewLabel('');
    emit(next, fallback, otherLabel);
  }

  function removeMember(src: string): void {
    const next = { ...mapping };
    delete next[src];
    setMapping(next);
    emit(next, fallback, otherLabel);
  }

  function deleteGroup(label: string): void {
    const next: Record<string, string> = {};
    for (const [src, dst] of Object.entries(mapping)) {
      if (dst !== label) next[src] = dst;
    }
    setMapping(next);
    emit(next, fallback, otherLabel);
  }

  function renameGroup(oldLabel: string, newLabelText: string): void {
    const trimmed = newLabelText.trim();
    if (!trimmed || trimmed === oldLabel) return;
    const next: Record<string, string> = {};
    for (const [src, dst] of Object.entries(mapping)) {
      next[src] = dst === oldLabel ? trimmed : dst;
    }
    setMapping(next);
    emit(next, fallback, otherLabel);
  }

  // Existing values side: every distinct value plus any source that was
  // mapped but no longer appears in the raw column (so the user can still
  // see/remove stale mappings).
  const allValues = useMemo(() => {
    const set = new Set<string>(distinct);
    for (const src of Object.keys(mapping)) set.add(src);
    return Array.from(set);
  }, [distinct, mapping]);

  const visibleValues = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allValues;
    return allValues.filter((v) => v.toLowerCase().includes(q));
  }, [allValues, search]);

  // Group view from mapping.
  const groups = useMemo(() => {
    const byLabel = new Map<string, string[]>();
    for (const [src, dst] of Object.entries(mapping)) {
      const list = byLabel.get(dst);
      if (list) list.push(src);
      else byLabel.set(dst, [src]);
    }
    for (const list of byLabel.values()) list.sort();
    return Array.from(byLabel.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [mapping]);

  const canGroup = newLabel.trim().length > 0 && selected.size > 0;

  return (
    <div className="flex flex-col gap-3 text-xs">
      <div className="flex gap-3 min-h-0">
        {/* Left — existing values with checkboxes */}
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <div className="flex items-center justify-between">
            <div
              className="text-[10px] uppercase"
              style={{ color: 'var(--color-muted)' }}
            >
              {t('valueGroupings.map.existingValues')}
              {!distinctLoading && (
                <span className="ml-1 normal-case">
                  ({visibleValues.length}
                  {visibleValues.length !== allValues.length
                    ? ` / ${allValues.length}`
                    : ''}
                  )
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => selectAllVisible(visibleValues)}
                disabled={visibleValues.length === 0}
                className="text-[11px] px-1.5 py-0.5 rounded hover:opacity-80 disabled:opacity-40"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('valueGroupings.map.selectAllVisible')}
              </button>
              {selected.size > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-[11px] px-1.5 py-0.5 rounded hover:opacity-80"
                  style={{ color: 'var(--color-muted)' }}
                >
                  {t('valueGroupings.map.clearSelection', { n: selected.size })}
                </button>
              )}
            </div>
          </div>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('valueGroupings.map.searchValue')}
            className="p-1.5 rounded"
            style={{ border: '1px solid var(--color-border)' }}
          />

          <div
            className="flex-1 overflow-auto rounded p-1"
            style={{
              border: '1px solid var(--color-border)',
              minHeight: '280px',
              maxHeight: '46vh',
            }}
          >
            {distinctLoading && (
              <div
                className="p-2"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('valueGroupings.map.loading')}
              </div>
            )}
            {!distinctLoading && visibleValues.length === 0 && (
              <div
                className="p-2"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('valueGroupings.map.noValues')}
              </div>
            )}
            {!distinctLoading &&
              visibleValues.map((v) => {
                const groupLabel = mapping[v];
                const isChecked = selected.has(v);
                return (
                  <label
                    key={v}
                    className="flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer hover:opacity-80"
                    style={{
                      background: isChecked
                        ? 'var(--color-surface)'
                        : undefined,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSelected(v)}
                    />
                    <span
                      className="flex-1 truncate"
                      style={{ color: 'var(--color-fg)' }}
                      title={v}
                    >
                      {v}
                    </span>
                    {groupLabel && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded truncate max-w-[120px]"
                        style={{
                          background: 'var(--color-surface)',
                          color: 'var(--color-muted)',
                          border: '1px solid var(--color-border)',
                        }}
                        title={groupLabel}
                      >
                        {groupLabel}
                      </span>
                    )}
                  </label>
                );
              })}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder={t('valueGroupings.map.newLabelPlaceholder')}
              className="flex-1 p-1.5 rounded"
              style={{ border: '1px solid var(--color-border)' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canGroup) {
                  e.preventDefault();
                  groupSelected();
                }
              }}
            />
            <button
              type="button"
              onClick={groupSelected}
              disabled={!canGroup}
              className="text-xs px-3 py-1.5 rounded text-white disabled:opacity-40"
              style={{ background: 'var(--color-accent)' }}
            >
              {t('valueGroupings.map.groupSelected')}
            </button>
          </div>
        </div>

        {/* Right — existing groups */}
        <div
          className="w-[300px] flex flex-col gap-2 min-h-0 p-2 rounded"
          style={{ border: '1px solid var(--color-border)' }}
        >
          <div
            className="text-[10px] uppercase"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('valueGroupings.map.groups', { n: groups.length })}
          </div>
          <div
            className="flex-1 overflow-auto flex flex-col gap-2"
            style={{ minHeight: '280px', maxHeight: '46vh' }}
          >
            {groups.length === 0 && (
              <div
                className="p-2"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('valueGroupings.map.noGroupsYet')}
              </div>
            )}
            {groups.map(([label, members]) => (
              <GroupCard
                key={label}
                label={label}
                members={members}
                onRename={(next) => renameGroup(label, next)}
                onRemoveMember={removeMember}
                onDelete={() => deleteGroup(label)}
              />
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}

function GroupCard({
  label,
  members,
  onRename,
  onRemoveMember,
  onDelete,
}: {
  label: string;
  members: string[];
  onRename: (next: string) => void;
  onRemoveMember: (src: string) => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(label);

  useEffect(() => {
    setDraft(label);
  }, [label]);

  return (
    <div
      className="flex flex-col gap-1.5 p-2 rounded"
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => onRename(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
            }
          }}
          className="flex-1 p-1 rounded text-xs font-semibold"
          style={{
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg)',
            color: 'var(--color-fg)',
          }}
        />
        <button
          type="button"
          onClick={onDelete}
          aria-label={t('valueGroupings.map.deleteGroup')}
          className="text-xs px-1.5 py-1 rounded hover:opacity-80"
          style={{ color: 'var(--color-danger)' }}
          title={t('valueGroupings.map.deleteGroup')}
        >
          ×
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {members.map((m) => (
          <span
            key={m}
            className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded"
            style={{
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-fg)',
              maxWidth: '100%',
            }}
            title={m}
          >
            <span className="truncate max-w-[180px]">{m}</span>
            <button
              type="button"
              onClick={() => onRemoveMember(m)}
              aria-label={t('valueGroupings.map.removeMember', { value: m })}
              className="opacity-60 hover:opacity-100"
              style={{ color: 'var(--color-muted)' }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
